import { ExportPngInfo, InsightRendererProps } from '@ecosystem/newron-client-collections';
import React, { FC, useEffect, useRef } from 'react';
import { Provider } from 'react-redux';
import { renderLoading } from '@pages/Loading';
import Store from '@state/Store';
import { useFetchChartData } from '@utils/collectionUtils/hooks/UseFetchChartData';
import ExploreTrendsMarketCard, {
  PerformanceAnalysisMarketCardProps,
} from '../../trends-stack-market-card/ExploreTrendsMarketCard';
import { TrendStackBarChartInsight } from './Model';
import CountryWarningBanner from '../warning-banner/country-warning-banner/CountryWarningBanner';
import { WarningBannerProps } from '../warning-banner/Types';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import PgWarningBanner from '@components/collections/warning-banner/pg-warning-banner/PgWarningBanner';
import { getExploreTrendsStackChart } from '@services/explore/Explore.service';
import { getBaseTimePeriodForMATAndYTD, getPeriodicityForMATAndYTD } from '@utils/PeriodUtils';
import { getIsUsingCollectionLiveData, getPeriodLabel } from '@utils/collectionUtils/CollectionUtils';
import { getCountryCodes } from '@utils/CountryUtils';
import MultiStackedChartExportProvider from '@hooks/export/export-stacked-chart/context/MultiStackedChartExportProvider';
import { modifiedPAORSegmentExportStackChartGrandTotalToCamelCaseTotalValue } from '@utils/TotalUtils';
import { PageType } from '@utils/pageConfig/Types';
import ExploreTrendsRetailerCard, {
  PerformanceAnalysisRetailerCardProps,
} from '@components/trends-stack-retailer-card/ExploreTrendsRetailerCard';
import { enableChartsExporting } from '@ecosystem/export-utils';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { processExportedImages } from '@hooks/export/ExportChart';
import { exportChartKey } from '@data-visualization/trends-stack-chart/StackedChartUtilities';
import { SaleType } from '@models/Products';
import { getRequireAllPgLoadedInCollections } from '@components/collections/CollectionsFlagsByInsightType';
import { CollectionInsightType } from '@components/collections/Model';

enableChartsExporting();

type TrendStackBarChartInsightRendererType = InsightRendererProps<TrendStackBarChartInsight>;

const TrendStackBarChartInsightContainer: FC<TrendStackBarChartInsightRendererType> = ({
  insight,
  onLoaded,
  onExportReady,
}) => {
  const { exportImages } = useExportImages(PageType.EXPLORE);
  const exportProcessed = useRef(false);
  const {
    apiParams,
    selectedGrowthType,
    selectedSaleType,
    selectedRetailerKpiOption,
    currencySymbol,
    selectedAbsoluteType,
  } = insight.data;

  const baseTimePeriod = getBaseTimePeriodForMATAndYTD(apiParams.period, apiParams.periodType);
  const { periodicity } = getPeriodicityForMATAndYTD(baseTimePeriod, apiParams.periodType);

  const { isLoading, chartData } = useFetchChartData({
    apiRequest: async () => {
      const label = await getPeriodLabel({
        clientId: apiParams.clientId,
        periodicity,
        baseTimePeriod,
        comparisonPeriod: apiParams.comparisonPeriod,
        countryIds: getCountryCodes(apiParams.countries),
        productType: apiParams.productType,
        collectionDataType: getIsUsingCollectionLiveData(apiParams.period, insight.dataType)
          ? 'live'
          : insight.dataType,
        productGroups: apiParams.productGroup,
      });
      const getPeriodFromLabel = RegExp(/\b\w{3,9} \d{4}(?= vs)/i).exec(label)?.[0] ?? apiParams.period;
      const isUsingCollectionLiveData = getIsUsingCollectionLiveData(getPeriodFromLabel, insight.dataType);
      return getExploreTrendsStackChart({
        ...apiParams,
        period: getPeriodFromLabel,
        isUsingCollectionLiveData: isUsingCollectionLiveData,
        saleFormat: selectedAbsoluteType,
        saleType: selectedSaleType === SaleType.volume ? 'VOLUME' : 'VALUE',
      });
    },
    onLoaded: onLoaded,
  });

  const modifiedData = chartData
    ? modifiedPAORSegmentExportStackChartGrandTotalToCamelCaseTotalValue(chartData)
    : {
        salesRecordsFound: false,
        categories: [],
        marketRevenueChange: [],
        marketTotalRevenueInMillion: [],
        retailerRevenueChange: [],
        retailerTotalRevenueInMillion: [],
        series: [],
      };

  const marketCardProps: PerformanceAnalysisMarketCardProps = {
    data: modifiedData,
    selectedGrowthType: selectedGrowthType,
    selectedPeriodicity: apiParams.periodType,
    currencySymbol: currencySymbol,
    selectedSaleType: selectedSaleType,
    selectedAbsoluteType: selectedAbsoluteType,
    noResultHandler: () => {},
  };

  const retailerCardProps: PerformanceAnalysisRetailerCardProps = {
    data: modifiedData,
    selectedGrowthType: selectedGrowthType,
    selectedPeriodicity: apiParams.periodType,
    currencySymbol: currencySymbol,
    selectedSaleType: selectedSaleType,
    selectedAbsoluteType: selectedAbsoluteType,
    selectedRetailerOption: selectedRetailerKpiOption,
    setSelectedRetailerOption: () => {},
    onChangeSelectedRetailerOption: () => {},
    excludeFilter: true,
    noResultHandler: () => {},
  };

  //region export from collections
  const chartImages = [
    exportImages[exportChartKey(modifiedData, selectedAbsoluteType, selectedSaleType, true)],
    exportImages[exportChartKey(modifiedData, selectedAbsoluteType, selectedSaleType, false)],
  ];

  useEffect(() => {
    const exportChart = async () => {
      if (chartImages.length && !chartImages.some((image) => image === undefined) && !exportProcessed.current) {
        const processedImages = await processExportedImages(chartImages);
        exportProcessed.current = true; // Ensure the export only happens once
        const exportImagesAsPowerPoint = async (): Promise<ExportPngInfo> => {
          return { images: processedImages };
        };
        onExportReady({ exportPng: exportImagesAsPowerPoint });
      }
    };
    exportChart();
  }, [chartImages, onExportReady]);
  //endregion

  if (isLoading) {
    return renderLoading();
  }

  return (
    <>
      <ExploreTrendsMarketCard {...marketCardProps} />
      <ExploreTrendsRetailerCard {...retailerCardProps} />
    </>
  );
};

export const TrendStackBarChartInsightRenderer: FC<TrendStackBarChartInsightRendererType> = (props) => {
  const {
    apiParams: { clientId, period, periodType, countries, productGroup, productType },
  } = props.insight.data;

  const warningBannerProps: WarningBannerProps = {
    clientId: clientId,
    countries: countries.map(({ id }) => ({ value: id, label: id })),
    productGroups: productGroup ?? [],
    period: period,
    periodicity: periodType,
    comparisonPeriod: RelativePeriod.PreviousYearCurrentPeriod,
    productType,
    collectionDataType: 'live', // in line chart, live = static
  };
  const isMultiCountrySelected = countries?.length > 1;
  const requireAllPgLoadedInCollections = getRequireAllPgLoadedInCollections(CollectionInsightType.TrendStackBarChart);

  return (
    <Provider store={Store}>
      <MultiStackedChartExportProvider>
        {isMultiCountrySelected && (
          <CountryWarningBanner warningBannerProps={warningBannerProps}>
            <TrendStackBarChartInsightContainer {...props} />
          </CountryWarningBanner>
        )}

        {!isMultiCountrySelected && (
          <PgWarningBanner
            baseApiParams={props.insight.data.apiParams}
            collectionDataType={props.insight.dataType}
            requireAllPgLoadedInCollections={requireAllPgLoadedInCollections}
          >
            <TrendStackBarChartInsightContainer {...props} />
          </PgWarningBanner>
        )}
      </MultiStackedChartExportProvider>
    </Provider>
  );
};
